/**
 * @file Dashboard metric card component.
 * @description Reusable KPI card for dashboard statistics with icon, value, and optional change indicator.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @version 0.0.0
 */
import React from 'react';
import { LucideIcon } from 'lucide-react';

export interface DashboardCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  gradient: string;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  value,
  icon: Icon,
  change,
  changeType = 'neutral',
  gradient,
}) => {
  const changeColorClass = {
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600',
  }[changeType];

  return (
    <div className="bg-white rounded-lg hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border-gray-200 border-1">
      <div className="p-3 flex-col flex sm:flex-row items-center justify-center sm:justify-start text-center sm:text-left gap-3">

        {/* Icon center aligned */}
        <div className={`p-4 rounded-full bg-gradient-to-r ${gradient}`}>
          <Icon size={28} className="text-white" />
        </div>

        <div className="flex flex-col">
          {/* Value (smaller than before) */}
          <h5 className="mt-1 text-2xl font-bold text-gray-900">
            {value}
          </h5>

          {/* Title (smaller font) */}
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            {title}
          </p>

          {/* Change text */}
          {change && (
            <p className={`mt-1 text-xs ${changeColorClass}`}>
              {change}
            </p>
          )}
        </div>


      </div>
    </div>
  );
};

export default DashboardCard;
